import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { viewCouponCodeDetails } from '../../slice/couponCodeSlice'

const ViewCouponCodeDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    
    // Get couponId from state
    const couponId = location.state?.couponId

    const { couponCodeDetail } = useSelector(state => state.couponCode)

    useEffect(() => {
        if (!couponId) {
            navigate('/coupons')
            return
        }
        dispatch(viewCouponCodeDetails({ id: couponId }))
    }, [couponId, dispatch, navigate])

    if (!couponCodeDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button 
                    className="btn btn-primary mt-3" 
                    onClick={() => navigate('/coupons')}
                >
                    Go Back to Coupons
                </button>
            </div>
        )
    }

    const {
        name,
        description,
        valid_from,
        valid_to,
        discount_percent,
        is_active,
        is_deleted,
        createdAt,
        updatedAt,
    } = couponCodeDetail

    // Calculate duration
    const getDuration = () => {
        if (valid_from && valid_to) {
            const start = moment(valid_from)
            const end = moment(valid_to)
            const days = end.diff(start, 'days')
            const hours = end.diff(start, 'hours') % 24
            
            if (days > 0) {
                return `${days} day${days !== 1 ? 's' : ''} ${hours > 0 ? `${hours} hour${hours !== 1 ? 's' : ''}` : ''}`
            } else {
                return `${hours} hour${hours !== 1 ? 's' : ''}`
            }
        }
        return 'NA'
    }

    // Check if coupon is expired
    const isExpired = () => {
        return moment(valid_to).isBefore(moment())
    }

    // Check if coupon is upcoming
    const isUpcoming = () => {
        return moment(valid_from).isAfter(moment())
    }

    // Check if coupon is currently valid
    const isCurrentlyValid = () => {
        const now = moment()
        return now.isAfter(moment(valid_from)) && now.isBefore(moment(valid_to))
    }

    // Get validity status badge
    const getValidityBadge = () => {
        if (isExpired()) {
            return (
                <span className="badge bg-danger">
                    <i className="fe-x-circle" style={{ fontSize: '10px' }}></i> Expired
                </span>
            )
        } else if (isUpcoming()) {
            return (
                <span className="badge bg-info">
                    <i className="fe-clock" style={{ fontSize: '10px' }}></i> Upcoming
                </span>
            )
        } else if (isCurrentlyValid()) {
            return (
                <span className="badge bg-success">
                    <i className="fe-check-circle" style={{ fontSize: '10px' }}></i> Active & Valid
                </span>
            )
        }
        return null
    }

    // Calculate time remaining or time until start
    const getTimeInfo = () => {
        const now = moment()
        
        if (isExpired()) {
            const expiredAgo = moment.duration(now.diff(moment(valid_to)))
            const days = Math.floor(expiredAgo.asDays())
            if (days > 0) {
                return `Expired ${days} day${days !== 1 ? 's' : ''} ago`
            } else {
                const hours = Math.floor(expiredAgo.asHours())
                return `Expired ${hours} hour${hours !== 1 ? 's' : ''} ago`
            }
        } else if (isUpcoming()) {
            const timeUntil = moment.duration(moment(valid_from).diff(now))
            const days = Math.floor(timeUntil.asDays())
            if (days > 0) {
                return `Starts in ${days} day${days !== 1 ? 's' : ''}`
            } else {
                const hours = Math.floor(timeUntil.asHours())
                return `Starts in ${hours} hour${hours !== 1 ? 's' : ''}`
            }
        } else if (isCurrentlyValid()) {
            const timeRemaining = moment.duration(moment(valid_to).diff(now))
            const days = Math.floor(timeRemaining.asDays())
            if (days > 0) {
                return `${days} day${days !== 1 ? 's' : ''} remaining`
            } else {
                const hours = Math.floor(timeRemaining.asHours())
                return `${hours} hour${hours !== 1 ? 's' : ''} remaining`
            }
        }
        return null
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Coupon Code Details</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/coupons')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Coupon Preview Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                <div className="card-body p-5">
                    <div className="text-center">
                        <div
                            style={{
                                display: 'inline-block',
                                backgroundColor: 'white',
                                padding: '40px 60px',
                                borderRadius: '16px',
                                border: '3px dashed #28a745',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                position: 'relative'
                            }}
                        >
                            {/* Status badges on coupon */}
                            <div style={{ position: 'absolute', top: '15px', right: '15px' }}>
                                {getValidityBadge()}
                                {is_active === 1 ? (
                                    <span className="badge bg-success ms-2">Active</span>
                                ) : (
                                    <span className="badge bg-secondary ms-2">Inactive</span>
                                )}
                            </div>

                            <div
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: '40px',
                                    fontWeight: '700',
                                    color: '#495057',
                                    letterSpacing: '3px',
                                    marginBottom: '20px'
                                }}
                            >
                                {name}
                            </div>
                            
                            <div
                                style={{
                                    fontSize: '64px',
                                    fontWeight: '700',
                                    color: '#28a745',
                                    marginBottom: '15px'
                                }}
                            >
                                {discount_percent}% OFF
                            </div>
                            
                            {description && (
                                <div
                                    style={{
                                        fontSize: '16px',
                                        color: '#6c757d',
                                        maxWidth: '500px',
                                        margin: '0 auto',
                                        lineHeight: '1.5'
                                    }}
                                >
                                    {description}
                                </div>
                            )}

                            {/* Time info at bottom of coupon */}
                            {getTimeInfo() && (
                                <div
                                    style={{
                                        marginTop: '20px',
                                        padding: '8px 16px',
                                        backgroundColor: isExpired() ? '#f8d7da' : isUpcoming() ? '#d1ecf1' : '#d4edda',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: isExpired() ? '#721c24' : isUpcoming() ? '#0c5460' : '#155724'
                                    }}
                                >
                                    {getTimeInfo()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Details Section */}
            <div className="row">
                {/* Coupon Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Coupon Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Coupon Code</small>
                                <div
                                    style={{
                                        fontFamily: 'monospace',
                                        fontSize: '18px',
                                        fontWeight: '700',
                                        color: '#495057',
                                        backgroundColor: '#f8f9fa',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px dashed #dee2e6',
                                        display: 'inline-block'
                                    }}
                                >
                                    {name}
                                </div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Description</small>
                                <strong>{description || 'NA'}</strong>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Discount Percentage</small>
                                <div>
                                    <span
                                        style={{
                                            fontWeight: '700',
                                            fontSize: '24px',
                                            color: '#28a745',
                                            backgroundColor: '#d4edda',
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            border: '2px solid #c3e6cb',
                                            display: 'inline-block'
                                        }}
                                    >
                                        {discount_percent}% OFF
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Validity Period */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Validity Period
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Valid From</small>
                                <strong>
                                    {valid_from
                                        ? moment(valid_from).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Valid To</small>
                                <strong>
                                    {valid_to
                                        ? moment(valid_to).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Duration</small>
                                <strong>{getDuration()}</strong>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Current Status</small>
                                <div className="mt-1">
                                    {getValidityBadge()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Status Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Active Status</small>
                                <div className="mt-1">
                                    {is_active === 1 ? (
                                        <span className="badge bg-success">Active</span>
                                    ) : (
                                        <span className="badge bg-secondary">Inactive</span>
                                    )}
                                </div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Deletion Status</small>
                                <div className="mt-1">
                                    {is_deleted === 1 ? (
                                        <span className="badge bg-danger">Deleted</span>
                                    ) : (
                                        <span className="badge bg-success">Active</span>
                                    )}
                                </div>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Validity Status</small>
                                <div className="mt-1">
                                    {isExpired() && (
                                        <span className="badge bg-danger">
                                            <i className="fe-x-circle" style={{ fontSize: '10px' }}></i> Expired
                                        </span>
                                    )}
                                    {isUpcoming() && (
                                        <span className="badge bg-info">
                                            <i className="fe-clock" style={{ fontSize: '10px' }}></i> Upcoming
                                        </span>
                                    )}
                                    {isCurrentlyValid() && (
                                        <span className="badge bg-success">
                                            <i className="fe-check-circle" style={{ fontSize: '10px' }}></i> Currently Valid
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Record Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Record Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Created At</small>
                                <strong>
                                    {createdAt
                                        ? moment(createdAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Last Updated</small>
                                <strong>
                                    {updatedAt
                                        ? moment(updatedAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Last Modified</small>
                                <strong>
                                    {updatedAt
                                        ? moment(updatedAt).fromNow()
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Usage Statistics (Placeholder) */}
                <div className="col-12 mb-4">
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            <i className="fe-bar-chart-2 me-2"></i>
                            Usage Information
                        </div>
                        <div className="card-body">
                            <div className="alert alert-info mb-0" style={{ borderRadius: '6px' }}>
                                <i className="fe-info me-2"></i>
                                Usage statistics and redemption details will be displayed here when available.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ViewCouponCodeDetail;